import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, resolveMediaUrl } from '../api/client';
import { stashClips, peekClips, normalizeStashedClips } from '../clipStore';
import { reelsPath } from '../paths';
import {
  canUseVoice,
  getSpeechRecognition,
  parseVoiceCommand,
  primeMicrophoneForRecognition,
  speak,
  speechRecognitionErrorMessage,
  unlockSpeechSynthesis,
  voiceUnsupportedReason,
} from '../voiceCommands';

import {
  actionNowText,
  CAPTURE_STAGES,
  PROCESS_MIN,
  stageDone,
} from '../captureFlowGuide';
import {
  CaptureFlowGuideModal,
  CaptureFlowRail,
  CapturePreviewCta,
  CapturePreviewModal,
  CaptureShotChecklist,
  CaptureStageTipModal,
} from '../components/CaptureFlowGuide';
import ConfirmModal from '../components/ConfirmModal';

const STAGES = CAPTURE_STAGES;
const FACING_USER = 'user';
const FACING_ENVIRONMENT = 'environment';
/** Capture at 720p vertical — much smaller uploads; JSON2Video outputs final 1080 reel. */
const REEL_W = 720;
const REEL_H = 1280;
/** ~2 Mbps keeps 6s clips near ~1.5MB so save is fast on kiosk networks. */
const REEL_CAPTURE_BPS = 2_000_000;

function revokeBlobUrl(url) {
  if (url && String(url).startsWith('blob:')) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }
}

function isActiveClip(clip) {
  if (!clip || clip.purged) return false;
  const status = String(clip.validation_status || 'pass').toLowerCase();
  return status !== 'superseded' && status !== 'error' && status !== 'retake';
}

/**
 * Map camera into 9:16 encode canvas. Wide stream first (see getUserMedia) — avoid digital zoom.
 * Portrait sensor: contain. Landscape sensor: mild center-biased cover (not tight top crop).
 */
function drawPortraitReelFrame(ctx, video, tw, th, focus = 'center') {
  const vw = video.videoWidth || 0;
  const vh = video.videoHeight || 0;
  if (!vw || !vh) return false;
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, tw, th);
  if (vh >= vw) {
    const scale = Math.min(tw / vw, th / vh);
    const dw = vw * scale;
    const dh = vh * scale;
    ctx.drawImage(video, 0, 0, vw, vh, (tw - dw) / 2, (th - dh) / 2, dw, dh);
    return true;
  }
  const scale = Math.max(tw / vw, th / vh);
  const sw = tw / scale;
  const sh = th / scale;
  let sx = (vw - sw) / 2;
  let sy = (vh - sh) / 2;
  if (focus === 'top') {
    sy = Math.max(0, Math.min(vh - sh, (vh - sh) * 0.28));
  } else if (focus === 'bottom') {
    sy = Math.max(0, vh - sh);
  }
  sx = Math.max(0, Math.min(vw - sw, sx));
  sy = Math.max(0, Math.min(vh - sh, sy));
  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, tw, th);
  return true;
}

function pickRecorderMime() {
  const candidates = [
    'video/mp4;codecs=avc1',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4',
  ];
  for (const mime of candidates) {
    try {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime)) {
        return mime;
      }
    } catch {
      /* ignore */
    }
  }
  return '';
}

function playBeep(kind = 'start') {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = kind === 'end' ? 880 : 660;
    gain.gain.value = 0.08;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + (kind === 'end' ? 0.12 : 0.08));
    osc.onended = () => ctx.close();
  } catch {
    /* optional */
  }
  if (kind === 'start' && navigator.vibrate) {
    navigator.vibrate(40);
  }
}

function nextStageLabel(stageIndex) {
  if (stageIndex >= STAGES.length - 1) return 'Create my reel';
  return `Next: ${STAGES[stageIndex + 1].shortLabel}`;
}

function recordButtonLabel(stage, localStatus, recording, remainSec, stageCaptured) {
  if (recording) return `Recording… ${remainSec != null ? `${remainSec}s` : ''}`.trim();
  if (stage.id === 'process') {
    if (localStatus.process >= 1) return 'Record another clip';
    return 'Start recording';
  }
  return stageCaptured ? 'Record again' : 'Start recording';
}

export default function CaptureFlow() {
  const { sessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const canvasStreamRef = useRef(null);
  const drawRafRef = useRef(0);
  const captureFocusRef = useRef('top');
  const recordAbortRef = useRef(false);
  const recordPausedRef = useRef(false);
  const recognitionRef = useRef(null);
  const voicePausedRef = useRef(false);
  const handlersRef = useRef({});
  /** Next process sequence index (synced from server on load). */
  const processSeqRef = useRef(0);
  const retakeSeqRef = useRef(null);
  const [retakeSeq, setRetakeSeq] = useState(null);
  const [processClips, setProcessClips] = useState([]);
  const [shotChecks, setShotChecks] = useState({ frame: false, light: false, steady: false });
  /** @type {React.MutableRefObject<{ before: Blob|null, process: Blob[], result: Blob|null }>} */
  const clipsRef = useRef({ before: null, process: [], result: null });

  const [stageIndex, setStageIndex] = useState(0);
  const [countdown, setCountdown] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordingPaused, setRecordingPaused] = useState(false);
  const [remainSec, setRemainSec] = useState(null);
  const [error, setError] = useState('');
  const [serviceType, setServiceType] = useState(location.state?.service_type || 'haircut');
  const [tipOpen, setTipOpen] = useState(false);
  const [flowIntroOpen, setFlowIntroOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [confirmAfterOpen, setConfirmAfterOpen] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const [voiceSupported] = useState(() => canUseVoice());
  const [heard, setHeard] = useState('');
  const [facingMode, setFacingMode] = useState(FACING_USER);
  const [clipPreviews, setClipPreviews] = useState({ before: null, process: [], result: null });
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [activePreviewUrl, setActivePreviewUrl] = useState('');
  const [localStatus, setLocalStatus] = useState({
    before: false,
    process: 0,
    result: false,
  });

  const stage = STAGES[stageIndex];
  const isLastStage = stageIndex === STAGES.length - 1;
  const stageCaptured = stageDone(stage, localStatus);
  const busy = recording || countdown != null;
  const canAddMore = stage.id === 'process' && localStatus.process > 0;
  const processShort =
    stage.id === 'process' && localStatus.process > 0 ? `${localStatus.process} ready` : '';

  const say = useCallback(async (text) => {
    voicePausedRef.current = true;
    recognitionRef.current?.stop?.();
    await speak(text);
    await wait(280);
    voicePausedRef.current = false;
    try {
      recognitionRef.current?.start?.();
    } catch {
      /* already started */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const session = await api.getSession(sessionId);
        if (cancelled) return;
        setServiceType(session.service_type || serviceType);

        // Prefer on-device clips (deferred upload) over server when present.
        const local = normalizeStashedClips(peekClips(sessionId));
        if (local?.before || local?.result || (local?.process?.length || 0) > 0) {
          clipsRef.current = {
            before: local.before,
            process: local.process.map((c) => ({
              sequence: c.sequence,
              blob: c.blob,
              previewUrl: URL.createObjectURL(c.blob),
            })),
            result: local.result,
            beforePreview: local.before ? URL.createObjectURL(local.before) : null,
            resultPreview: local.result ? URL.createObjectURL(local.result) : null,
          };
          const maxSeq = local.process.reduce((m, c) => Math.max(m, c.sequence), -1);
          processSeqRef.current = maxSeq + 1;
          setProcessClips(local.process.map((c) => ({ sequence: c.sequence })));
          setLocalStatus({
            before: Boolean(local.before),
            process: local.process.length,
            result: Boolean(local.result),
          });
          const nextPreviews = {
            before: clipsRef.current.beforePreview,
            process: clipsRef.current.process.map((c) => ({
              sequence: c.sequence,
              url: c.previewUrl,
            })),
            result: clipsRef.current.resultPreview,
          };
          setClipPreviews(nextPreviews);
          const firstPreview =
            nextPreviews.before || nextPreviews.process[0]?.url || nextPreviews.result || '';
          if (firstPreview) setActivePreviewUrl(firstPreview);
          if (!local.before) setStageIndex(0);
          else if (local.process.length < PROCESS_MIN) setStageIndex(1);
          else if (!local.result) setStageIndex(2);
          setFlowIntroOpen(false);
          setTipOpen(false);
          return;
        }

        const clips = (session.clips || []).filter(isActiveClip);
        const beforeClip = clips
          .filter((c) => c.clip_type === 'before')
          .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))[0];
        const processOk = clips
          .filter((c) => c.clip_type === 'process')
          .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        // Newest per sequence
        const processBySeq = new Map();
        processOk.forEach((c) => {
          const seq = Number(c.sequence) || 0;
          const prev = processBySeq.get(seq);
          if (!prev || String(c.created_at || '') >= String(prev.created_at || '')) {
            processBySeq.set(seq, c);
          }
        });
        const processList = [...processBySeq.values()].sort(
          (a, b) => (a.sequence || 0) - (b.sequence || 0)
        );
        const resultClip = clips
          .filter((c) => c.clip_type === 'result')
          .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))[0];
        const processCount = processList.length;
        const maxProcessSeq = processList.reduce(
          (max, c) => Math.max(max, Number(c.sequence) || 0),
          -1
        );
        processSeqRef.current = maxProcessSeq + 1;
        setProcessClips(processList.map((c) => ({ sequence: Number(c.sequence) || 0 })));
        setLocalStatus({
          before: Boolean(beforeClip),
          process: processCount,
          result: Boolean(resultClip),
        });
        const nextPreviews = {
          before: beforeClip?.preview_url ? resolveMediaUrl(beforeClip.preview_url) : null,
          process: processList
            .map((c) => ({
              sequence: Number(c.sequence) || 0,
              url: c.preview_url ? resolveMediaUrl(c.preview_url) : '',
            }))
            .filter((c) => c.url),
          result: resultClip?.preview_url ? resolveMediaUrl(resultClip.preview_url) : null,
        };
        setClipPreviews(nextPreviews);
        const firstPreview =
          nextPreviews.before || nextPreviews.process[0]?.url || nextPreviews.result || '';
        if (firstPreview) setActivePreviewUrl(firstPreview);
        if (session.status === 'rendering' || session.status === 'completed' || session.status === 'approved') {
          navigate(reelsPath('rendering', sessionId), { replace: true });
          return;
        }
        if (!beforeClip) setStageIndex(0);
        else if (processCount < PROCESS_MIN) setStageIndex(1);
        else if (!resultClip) setStageIndex(2);
        setFlowIntroOpen(false);
        setTipOpen(false);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    return () => {
      revokeBlobUrl(clipPreviews.before);
      revokeBlobUrl(clipPreviews.result);
      (clipPreviews.process || []).forEach((item) => revokeBlobUrl(item.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTipOpen(false);
  }, [stageIndex]);

  useEffect(() => {
    const stage = STAGES[stageIndex];
    captureFocusRef.current = stage?.id === 'nails' ? 'center' : 'center';
  }, [stageIndex]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // Wide FOV first — forcing 9:16 on the device often digital-zooms (face too close).
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facingMode },
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 },
          },
          audio: false,
        });
        if (!active) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        const track = stream.getVideoTracks()[0];
        try {
          const caps = track?.getCapabilities?.() || {};
          if (caps.zoom && typeof caps.zoom.min === 'number') {
            await track.applyConstraints({ advanced: [{ zoom: caps.zoom.min }] });
          }
        } catch {
          /* zoom not supported */
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play();
        }
        const canvas = canvasRef.current;
        if (canvas && video) {
          canvas.width = REEL_W;
          canvas.height = REEL_H;
          const ctx = canvas.getContext('2d', { alpha: false });
          const paint = () => {
            if (!active) return;
            if (ctx && video.readyState >= 2) {
              drawPortraitReelFrame(
                ctx,
                video,
                REEL_W,
                REEL_H,
                captureFocusRef.current
              );
            }
            drawRafRef.current = requestAnimationFrame(paint);
          };
          drawRafRef.current = requestAnimationFrame(paint);
          canvasStreamRef.current = canvas.captureStream(30);
        }
      } catch {
        if (active) {
          setError(
            facingMode === FACING_ENVIRONMENT
              ? 'Rear camera unavailable. Try Front or check camera permission.'
              : 'Camera access required for capture.'
          );
        }
      }
    })();
    return () => {
      active = false;
      if (drawRafRef.current) cancelAnimationFrame(drawRafRef.current);
      drawRafRef.current = 0;
      canvasStreamRef.current?.getTracks().forEach((track) => track.stop());
      canvasStreamRef.current = null;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [facingMode]);

  function stopCamera() {
    if (drawRafRef.current) cancelAnimationFrame(drawRafRef.current);
    drawRafRef.current = 0;
    canvasStreamRef.current?.getTracks().forEach((track) => track.stop());
    canvasStreamRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }

  function switchCamera() {
    if (busy) return;
    setError('');
    setFacingMode((prev) => (prev === FACING_USER ? FACING_ENVIRONMENT : FACING_USER));
  }

  async function startWithCountdown() {
    setError('');
    setTipOpen(false);
    voicePausedRef.current = true;
    try {
      recognitionRef.current?.stop?.();
    } catch {
      /* ignore */
    }
    await speak('Starting capture');
    setCountdown(3);
    for (const n of [3, 2, 1]) {
      setCountdown(n);
      if (n === 1) playBeep('start');
      await wait(700);
    }
    setCountdown(null);
    await recordClip();
  }

  function pauseOrResumeRecording() {
    const recorder = mediaRecorderRef.current;
    if (!recorder || !recording) return;
    try {
      if (recorder.state === 'recording' && typeof recorder.pause === 'function') {
        recorder.pause();
        recordPausedRef.current = true;
        setRecordingPaused(true);
        return;
      }
      if (recorder.state === 'paused' && typeof recorder.resume === 'function') {
        recorder.resume();
        recordPausedRef.current = false;
        setRecordingPaused(false);
      }
    } catch {
      /* ignore unsupported pause */
    }
  }

  function stopRecordingEarly() {
    if (!recording) return;
    recordPausedRef.current = false;
    setRecordingPaused(false);
    recordAbortRef.current = true;
    const recorder = mediaRecorderRef.current;
    try {
      if (recorder && recorder.state !== 'inactive') recorder.stop();
    } catch {
      /* ignore */
    }
  }

  async function recordClip() {
    // Prefer 9:16 canvas stream so uploaded clips match preview + render canvas.
    const stream = canvasStreamRef.current || streamRef.current;
    if (!stream) return;
    chunksRef.current = [];
    recordAbortRef.current = false;
    recordPausedRef.current = false;
    setRecordingPaused(false);
    const mime = pickRecorderMime();
    const recorder = mime
      ? new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: REEL_CAPTURE_BPS })
      : new MediaRecorder(stream, { videoBitsPerSecond: REEL_CAPTURE_BPS });
    mediaRecorderRef.current = recorder;
    recorder.ondataavailable = (event) => {
      if (event.data?.size) chunksRef.current.push(event.data);
    };
    const done = new Promise((resolve) => {
      recorder.onstop = resolve;
    });
    recorder.start(400);
    setRecording(true);

    const totalMs = stage.seconds * 1000;
    let remainingMs = totalMs;
    setRemainSec(stage.seconds);
    while (remainingMs > 0) {
      if (recordAbortRef.current) break;
      if (recordPausedRef.current) {
        setRemainSec(Math.max(0, Math.ceil(remainingMs / 1000)));
        await wait(200);
        continue;
      }
      const tick = 200;
      await wait(tick);
      remainingMs -= tick;
      setRemainSec(Math.max(0, Math.ceil(remainingMs / 1000)));
    }
    setRemainSec(0);
    if (recorder.state !== 'inactive') recorder.stop();
    await done;
    setRecording(false);
    setRecordingPaused(false);
    recordPausedRef.current = false;
    setRemainSec(null);
    playBeep('end');

    const blobType = recorder.mimeType || mime || 'video/webm';
    const blob = new Blob(chunksRef.current, { type: blobType });
    if (!blob.size) {
      setError('Recording was empty. Try again.');
      await say('Capture failed. Please try again.');
      return;
    }

    let completeLine = 'Capture complete';
    let sequence = 0;
    try {
      setError('');
      const localPreview = URL.createObjectURL(blob);
      setActivePreviewUrl(localPreview);
      setShotChecks({ frame: true, light: true, steady: true });

      // Keep clips on-device during capture — bulk upload happens on Create reel.
      if (stage.id === 'process') {
        sequence = retakeSeqRef.current != null ? retakeSeqRef.current : processSeqRef.current;
        const wasRetake = retakeSeqRef.current != null;
        const processEntries = [...(clipsRef.current.process || [])];
        const existingIdx = processEntries.findIndex((c) => c.sequence === sequence);
        if (existingIdx >= 0) {
          const old = processEntries[existingIdx];
          if (old?.previewUrl) revokeBlobUrl(old.previewUrl);
          processEntries[existingIdx] = { sequence, blob, previewUrl: localPreview };
        } else {
          processEntries.push({ sequence, blob, previewUrl: localPreview });
        }
        processEntries.sort((a, b) => a.sequence - b.sequence);
        clipsRef.current.process = processEntries;
        if (!wasRetake) {
          processSeqRef.current = sequence + 1;
          setLocalStatus((prev) => ({ ...prev, process: prev.process + 1 }));
        }
        setProcessClips(processEntries.map((c) => ({ sequence: c.sequence })));
        setClipPreviews((prev) => {
          const existing = prev.process.find((c) => c.sequence === sequence);
          if (existing && existing.url && existing.url !== localPreview) revokeBlobUrl(existing.url);
          const process = [
            ...prev.process.filter((c) => c.sequence !== sequence),
            { sequence, url: localPreview },
          ].sort((a, b) => a.sequence - b.sequence);
          return { ...prev, process };
        });
        retakeSeqRef.current = null;
        setRetakeSeq(null);
        const count = wasRetake ? localStatus.process : localStatus.process + 1;
        completeLine =
          count >= 1
            ? `Clip ${count} ready. Preview or record another — upload starts at the end.`
            : 'Clip ready. Tap Start recording to add more.';
      } else if (stage.id === 'before') {
        if (clipsRef.current.beforePreview) revokeBlobUrl(clipsRef.current.beforePreview);
        clipsRef.current.before = blob;
        clipsRef.current.beforePreview = localPreview;
        setLocalStatus((prev) => ({ ...prev, before: true }));
        setClipPreviews((prev) => {
          if (prev.before && prev.before !== localPreview) revokeBlobUrl(prev.before);
          return { ...prev, before: localPreview };
        });
        completeLine = 'Before ready. Tap Next — we upload everything when you create the reel.';
      } else {
        if (clipsRef.current.resultPreview) revokeBlobUrl(clipsRef.current.resultPreview);
        clipsRef.current.result = blob;
        clipsRef.current.resultPreview = localPreview;
        setLocalStatus((prev) => ({ ...prev, result: true }));
        setClipPreviews((prev) => {
          if (prev.result && prev.result !== localPreview) revokeBlobUrl(prev.result);
          return { ...prev, result: localPreview };
        });
        completeLine = 'After ready. Tap Create reel to upload and finish.';
      }

      stashClips(sessionId, {
        before: clipsRef.current.before,
        process: (clipsRef.current.process || []).map((c) => ({
          sequence: c.sequence,
          blob: c.blob,
        })),
        result: clipsRef.current.result,
      });
    } catch (e) {
      setError(e.message || 'Could not save clip on this device.');
      await say('Capture failed. Please try again.');
      return;
    }
    void say(completeLine);
  }

  async function goToAfterConfirmed() {
    setConfirmAfterOpen(false);
    await say(`Moving to step ${STAGES[2].step}: ${STAGES[2].label}`);
    setStageIndex(2);
    setError('');
  }

  function backToDuring() {
    setConfirmAfterOpen(false);
    setStageIndex(1);
    setError('');
    void say('Back to during. Record more clips, then Done with process when ready for After.');
  }

  async function nextStage() {
    const captured =
      stage.id === 'process'
        ? localStatus.process >= PROCESS_MIN
        : Boolean(localStatus[stage.id]);
    if (!captured) {
      await say(
        stage.id === 'process'
          ? 'Record at least one clip while working, then add more if you need.'
          : `Please record the ${stage.shortLabel.toLowerCase()} video first.`
      );
      return;
    }
    // Leaving During → After: confirm to avoid accidental skip of more process clips
    if (stage.id === 'process') {
      setConfirmAfterOpen(true);
      return;
    }
    if (!isLastStage) {
      await say(`Moving to step ${STAGES[stageIndex + 1].step}: ${STAGES[stageIndex + 1].label}`);
      setStageIndex((index) => index + 1);
      setError('');
      return;
    }
    await say('Uploading your clips and creating your reel.');
    finishCapture();
  }

  function finishCapture() {
    stopCamera();
    stopVoiceListening();
    try {
      sessionStorage.setItem('naturals_video_last_session', sessionId);
    } catch {
      /* ignore */
    }
    stashClips(sessionId, {
      before: clipsRef.current.before,
      process: (clipsRef.current.process || []).map((c) => ({
        sequence: c.sequence,
        blob: c.blob,
      })),
      result: clipsRef.current.result,
    });
    navigate(reelsPath('rendering', sessionId), { replace: true });
  }

  // Keep latest handlers for recognition callback
  handlersRef.current = {
    busy,
    stageCaptured,
    stageId: stage.id,
    processCount: localStatus.process,
    startWithCountdown,
    nextStage,
  };

  const handleVoiceCommand = useCallback(
    async (cmd) => {
      const h = handlersRef.current;
      if (!cmd || h.busy || voicePausedRef.current) return;

      if (cmd === 'capture' || cmd === 'add') {
        if (cmd === 'add' && h.stageId !== 'process') {
          await say('Add is only for process cuts. Say capture instead.');
          return;
        }
        if (cmd === 'add' && h.processCount < 1) {
          await say('Say capture for the first process cut.');
          return;
        }
        await h.startWithCountdown();
        return;
      }

      if (cmd === 'next') {
        await h.nextStage();
      }
    },
    [say]
  );

  function startVoiceListening() {
    if (!voiceSupported) {
      setError(voiceUnsupportedReason() || 'Voice commands need Chrome or Edge on this device.');
      return;
    }
    // Must run from user gesture — unlock TTS and prime mic before async recognition start.
    unlockSpeechSynthesis();
    (async () => {
      try {
        await primeMicrophoneForRecognition();
      } catch (e) {
        setError(e.message || 'Microphone permission needed for voice commands.');
        setVoiceOn(false);
        return;
      }

      try {
        recognitionRef.current?.stop?.();
      } catch {
        /* ignore */
      }
      const recognition = getSpeechRecognition();
      if (!recognition) {
        setError(voiceUnsupportedReason() || 'Voice not supported.');
        return;
      }

      recognition.lang = 'en-IN';
      recognition.continuous = true;
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      recognition.onresult = (event) => {
        if (voicePausedRef.current || handlersRef.current.busy) return;
        const result = event.results?.[event.results.length - 1];
        if (!result?.[0]) return;
        const transcript = result[0].transcript || '';
        setHeard(transcript);
        const cmd = parseVoiceCommand(transcript);
        if (cmd) handleVoiceCommand(cmd);
      };
      recognition.onerror = (event) => {
        const msg = speechRecognitionErrorMessage(event.error);
        if (msg) {
          setError(msg);
          if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
            setVoiceOn(false);
          }
        }
      };
      recognition.onend = () => {
        if (!voicePausedRef.current && recognitionRef.current === recognition) {
          try {
            recognition.start();
          } catch {
            /* ignore */
          }
        }
      };

      recognitionRef.current = recognition;
      voicePausedRef.current = false;
      try {
        recognition.start();
        setVoiceOn(true);
        setError('');
        await speak('Voice on. Say capture, next, or add.');
      } catch {
        setError('Could not start voice listening. Tap Mic again.');
        setVoiceOn(false);
      }
    })();
  }

  function stopVoiceListening() {
    voicePausedRef.current = true;
    try {
      recognitionRef.current?.stop?.();
    } catch {
      /* ignore */
    }
    recognitionRef.current = null;
    setVoiceOn(false);
    voicePausedRef.current = false;
  }

  useEffect(() => {
    return () => {
      try {
        recognitionRef.current?.stop?.();
      } catch {
        /* ignore */
      }
      window.speechSynthesis?.cancel?.();
    };
  }, []);

  function closeTip() {
    setTipOpen(false);
  }

  function dismissFlowIntro() {
    setFlowIntroOpen(false);
  }

  function startRetakeProcess(sequence) {
    retakeSeqRef.current = sequence;
    setRetakeSeq(sequence);
    setError('');
    setTipOpen(false);
  }

  useEffect(() => {
    setShotChecks({ frame: false, light: false, steady: false });
    setPreviewModalOpen(false);
  }, [stageIndex]);

  const recordLabel = recordButtonLabel(stage, localStatus, recording, remainSec, stageCaptured);
  const stagePreviewItems =
    stage.id === 'before' && clipPreviews.before
      ? [{ id: 'before', label: 'Before', url: clipPreviews.before }]
      : stage.id === 'result' && clipPreviews.result
        ? [{ id: 'result', label: 'After', url: clipPreviews.result }]
        : stage.id === 'process'
          ? clipPreviews.process.map((clip) => ({
              id: `process-${clip.sequence}`,
              label: `Clip ${clip.sequence + 1}`,
              url: clip.url,
              sequence: clip.sequence,
            }))
          : [];
  const previewTitle =
    stage.id === 'process' ? 'During clips' : stage.id === 'before' ? 'Before clip' : 'After clip';

  const openPreviewModal = () => {
    if (!stagePreviewItems.length) return;
    const preferred =
      stagePreviewItems.find((item) => item.url === activePreviewUrl)?.url ||
      stagePreviewItems[stagePreviewItems.length - 1]?.url ||
      '';
    setActivePreviewUrl(preferred);
    setPreviewModalOpen(true);
  };
  const nextLabel = nextStageLabel(stageIndex);

  return (
    <main className="capture-layout capture-layout--immersive capture-layout--voice">
      <div className="capture-title capture-title--compact capture-title--desktop">
        <p className="eyebrow">
          Step {stage.step} of {STAGES.length} · {serviceType.replace('_', ' ')}
        </p>
        <div className="capture-heading-row">
          <h1 className="brand capture-brand">{stage.label}</h1>
          <button
            type="button"
            className="tip-info-btn"
            aria-label="View recording guide"
            onClick={() => setGuideOpen(true)}
            disabled={busy}
          >
            ?
          </button>
          {voiceSupported ? (
            <button
              type="button"
              className={`voice-toggle${voiceOn ? ' on' : ''}`}
              onClick={() => (voiceOn ? stopVoiceListening() : startVoiceListening())}
              disabled={busy}
              aria-label={voiceOn ? 'Turn voice off' : 'Turn voice on'}
            >
              {voiceOn ? 'Mic on' : 'Mic'}
            </button>
          ) : null}
        </div>
        <p className="capture-action-now">{actionNowText(stage, localStatus, stageCaptured)}</p>
        {voiceOn ? (
          <p className="voice-hint">
            Voice on — say <strong>capture</strong> to record, <strong>next</strong> to continue
            {heard ? <span className="voice-heard"> · Heard: “{heard}”</span> : null}
          </p>
        ) : voiceSupported ? (
          <p className="voice-hint muted">Optional: tap Mic for hands-free voice control</p>
        ) : null}
      </div>

      <CaptureFlowRail stageIndex={stageIndex} localStatus={localStatus} />

      <CaptureShotChecklist checks={shotChecks} active={!flowIntroOpen} />

      <div className="camera-wrap camera-wrap--stage">
        {/* Hidden source — visible preview is the 9:16 encode canvas (WYSIWYG). */}
        <video
          ref={videoRef}
          className="reel-capture-source"
          playsInline
          muted
          autoPlay
        />
        <canvas
          ref={canvasRef}
          className={`reel-capture-canvas${facingMode === FACING_USER ? ' mirror' : ''}`}
          aria-hidden="true"
        />

        <div className="hud">
          <span className="pill pill-stage">
            {stage.shortLabel}
            {processShort ? ` · ${processShort}` : ''}
          </span>
          <div className="hud-end">
            <button
              type="button"
              className="tip-info-btn hud-tool"
              aria-label="View recording guide"
              onClick={() => setGuideOpen(true)}
              disabled={busy}
            >
              ?
            </button>
            <button
              type="button"
              className={`hud-tool cam-switch-btn${facingMode === FACING_ENVIRONMENT ? ' cam-switch-btn--rear' : ''}`}
              onClick={switchCamera}
              disabled={busy}
              aria-label={
                facingMode === FACING_USER ? 'Switch to rear camera' : 'Switch to front camera'
              }
              title={
                facingMode === FACING_USER ? 'Switch to rear camera' : 'Switch to front camera'
              }
            >
              {facingMode === FACING_USER ? 'Rear' : 'Front'}
            </button>
            {voiceSupported ? (
              <button
                type="button"
                className={`voice-toggle hud-tool${voiceOn ? ' on' : ''}`}
                onClick={() => (voiceOn ? stopVoiceListening() : startVoiceListening())}
                disabled={busy}
                aria-label={voiceOn ? 'Turn voice off' : 'Turn voice on'}
              >
                {voiceOn ? 'Mic on' : 'Mic'}
              </button>
            ) : null}
            <span className={`pill pill-status${recording ? ' pill-rec' : ''}${voiceOn && !recording ? ' pill-mic' : ''}`}>
              {recording ? (
                <>
                  <i className="rec-dot" />{' '}
                  {recordingPaused
                    ? 'Paused'
                    : remainSec != null
                      ? `${remainSec}s`
                      : 'Rec'}
                </>
              ) : voiceOn ? (
                'Listening…'
              ) : (
                facingMode === FACING_ENVIRONMENT ? 'Rear cam' : 'Ready'
              )}
            </span>
          </div>
        </div>

        {countdown != null ? <div className="countdown">{countdown}</div> : null}

        <div className="camera-actions">
          {recording ? (
            <>
              <button
                type="button"
                className="cam-text-btn cam-text-btn--secondary"
                onClick={pauseOrResumeRecording}
              >
                {recordingPaused ? 'Resume' : 'Pause'}
              </button>
              <button
                type="button"
                className="cam-text-btn cam-text-btn--capture"
                onClick={stopRecordingEarly}
              >
                Stop
              </button>
            </>
          ) : (
            <button
              type="button"
              className="cam-text-btn cam-text-btn--capture"
              disabled={busy}
              onClick={startWithCountdown}
            >
              {stage.id === 'process'
                ? localStatus.process >= 1
                  ? 'Record more'
                  : 'Start recording'
                : stageCaptured
                  ? 'Record again'
                  : 'Start recording'}
            </button>
          )}

          {stagePreviewItems.length ? (
            <button
              type="button"
              className="cam-text-btn cam-text-btn--preview"
              disabled={busy}
              onClick={openPreviewModal}
            >
              View preview
              {stagePreviewItems.length > 1 ? ` (${stagePreviewItems.length})` : ''}
            </button>
          ) : null}

          {stage.id === 'process' && localStatus.process >= PROCESS_MIN && !recording ? (
            <button
              type="button"
              className="cam-text-btn cam-text-btn--secondary"
              disabled={busy}
              onClick={nextStage}
            >
              Done with process
            </button>
          ) : null}

          {/* Process stage advances only via "Done with process" — hide Next step there. */}
          {stage.id !== 'process' ? (
            <button
              type="button"
              className="cam-text-btn cam-text-btn--next"
              disabled={!stageCaptured || busy}
              onClick={nextStage}
            >
              {isLastStage ? 'Create reel' : 'Next step'}
            </button>
          ) : null}
          {/* Intentionally no "Add during clips" on After — process adds happen only on During. */}
        </div>
      </div>

      <div className="actions actions--desktop">
        <button
          className="btn btn-secondary"
          type="button"
          disabled={busy}
          onClick={switchCamera}
        >
          {facingMode === FACING_USER ? 'Use rear camera' : 'Use front camera'}
        </button>
        {recording ? (
          <>
            <button className="btn btn-secondary" type="button" onClick={pauseOrResumeRecording}>
              {recordingPaused ? 'Resume' : 'Pause'}
            </button>
            <button className="btn btn-primary" type="button" onClick={stopRecordingEarly}>
              Stop
            </button>
          </>
        ) : (
          <button className="btn btn-primary" disabled={busy} onClick={startWithCountdown}>
            {recordLabel}
          </button>
        )}
        {canAddMore && !recording ? (
          <button className="btn btn-secondary" onClick={startWithCountdown} disabled={busy}>
            Record another clip
          </button>
        ) : null}
        {stage.id === 'process' && localStatus.process >= PROCESS_MIN && !recording ? (
          <button className="btn btn-secondary" type="button" disabled={busy} onClick={nextStage}>
            Done with process
          </button>
        ) : null}
        <CapturePreviewCta
          label="View preview"
          count={stagePreviewItems.length}
          onClick={openPreviewModal}
          disabled={busy}
        />
        {stage.id !== 'process' ? (
          <button className="btn btn-ghost" disabled={!stageCaptured || busy} onClick={nextStage}>
            {nextLabel}
          </button>
        ) : null}
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      {flowIntroOpen ? (
        <CaptureFlowGuideModal onClose={dismissFlowIntro} title="How to make your reel" />
      ) : null}

      {guideOpen ? (
        <CaptureFlowGuideModal onClose={() => setGuideOpen(false)} title="Recording guide" />
      ) : null}

      {tipOpen ? <CaptureStageTipModal stage={stage} onClose={closeTip} voiceSupported={voiceSupported} /> : null}

      <CapturePreviewModal
        open={previewModalOpen}
        title={previewTitle}
        items={stagePreviewItems}
        activeUrl={activePreviewUrl}
        onSelect={setActivePreviewUrl}
        onRetake={stage.id === 'process' ? startRetakeProcess : undefined}
        onClose={() => setPreviewModalOpen(false)}
        disabled={busy}
      />

      <ConfirmModal
        open={confirmAfterOpen}
        title="Move to After?"
        body={`You have ${localStatus.process} during clip${localStatus.process === 1 ? '' : 's'} ready. Go to After now, or stay to record more while you work.`}
        confirmLabel="Go to After"
        cancelLabel="Stay on During"
        onConfirm={goToAfterConfirmed}
        onCancel={() => setConfirmAfterOpen(false)}
      />
    </main>
  );
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
